import { FC } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import type { TFunction } from 'i18next';

interface SCUIReturnProductCopyButtonProps {
    product: any;
    imageUrl?: string;
    showLabel?: boolean;
    iconSize?: 'small' | 'medium' | 'large';
}

interface ProductCopyFields {
    returnReason: string;
    productName: string;
    productSize: string;
    barcode: string;
    specialCode: string;
    brandName: string;
}

const IMAGE_SIZE = 256;

const readBlobAsForcedPngDataUrl = (blob: Blob, blobUrl: string | null): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result as string;
            if (blobUrl) URL.revokeObjectURL(blobUrl);

            if (dataUrl.startsWith('data:')) {
                const base64 = dataUrl.split(',')[1];
                resolve(`data:image/png;base64,${base64}`);
                return;
            }

            resolve(dataUrl);
        };
        reader.onerror = () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
            reject(reader.error);
        };
        reader.readAsDataURL(blob);
    });

const drawBlobToPngDataUrl = (blob: Blob, blobUrl: string, img: HTMLImageElement): Promise<string> =>
    new Promise((resolve, reject) => {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = IMAGE_SIZE;
            canvas.height = IMAGE_SIZE;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                URL.revokeObjectURL(blobUrl);
                reject(new Error('Could not get canvas context'));
                return;
            }

            ctx.drawImage(img, 0, 0, IMAGE_SIZE, IMAGE_SIZE);
            const pngDataUrl = canvas.toDataURL('image/png', 1.0);
            URL.revokeObjectURL(blobUrl);
            resolve(pngDataUrl);
        } catch {
            readBlobAsForcedPngDataUrl(blob, blobUrl).then(resolve).catch(reject);
        }
    });

/** Convert image to PNG data URL for Outlook desktop compatibility. */
const convertImageToPngDataUrl = async (sourceImageUrl: string): Promise<string> => {
    const response = await fetch(sourceImageUrl);
    const blob = await response.blob();

    return new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        const blobUrl = URL.createObjectURL(blob);

        img.onload = () => {
            drawBlobToPngDataUrl(blob, blobUrl, img).then(resolve).catch(reject);
        };

        img.onerror = () => {
            readBlobAsForcedPngDataUrl(blob, blobUrl).then(resolve).catch(reject);
        };

        img.src = blobUrl;
    });
};

const resolveImageDataUrl = async (sourceImageUrl?: string): Promise<string> => {
    if (!sourceImageUrl) {
        return '';
    }

    try {
        return await convertImageToPngDataUrl(sourceImageUrl);
    } catch (imgErr) {
        console.warn('Could not convert image to data URL:', imgErr);
        return sourceImageUrl;
    }
};

const extractProductCopyFields = (product: any): ProductCopyFields => ({
    returnReason: product.reasonDescription || '',
    productName: product.productDescriptionOnWebSite || '',
    productSize: product.size || '',
    barcode: product.barcode || '',
    specialCode: `${product.ozelKod1 || ''} - ${product.renkKod || ''}`.trim(),
    brandName: product.brandName || '',
});

const renderLabeledValue = (label: string, value: string, extraStyle = '') => `
    <div style="font-weight: 700; margin-bottom: 2px;${extraStyle}">${label}</div>
    ${value ? `<div style="margin-bottom: 6px;">${value}</div>` : ''}
`;

const buildProductCopyHtml = (fields: ProductCopyFields, imageDataUrl: string, t: TFunction): string => {
    const imageHtml = imageDataUrl
        ? `<img src="${imageDataUrl}" width="${IMAGE_SIZE}" height="${IMAGE_SIZE}" style="display: block; width: ${IMAGE_SIZE}px; height: ${IMAGE_SIZE}px; object-fit: contain; border: 0;" alt="Product Image" />`
        : '';

    const specialCodeValue = fields.specialCode && fields.specialCode !== '-' ? fields.specialCode : '';

    return `
        <html>
        <head>
            <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
        </head>
        <body style="font-family: Arial, sans-serif; margin: 0; padding: 16px;">
            <table border="0" cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
                <tr>
                    <td valign="top" style="padding-right: 16px;">
                        ${imageHtml}
                    </td>
                    <td valign="top" style="padding-right: 24px;">
                        <div style="font-size: 14px; line-height: 1.5;">
                            ${renderLabeledValue(t('general.returnReason'), fields.returnReason)}
                            ${renderLabeledValue(t('general.productName'), fields.productName, ' margin-top: 6px;')}
                            ${renderLabeledValue(t('general.productSize'), fields.productSize, ' margin-top: 6px;')}
                        </div>
                    </td>
                    <td valign="top">
                        <div style="font-size: 14px; line-height: 1.5;">
                            ${renderLabeledValue(t('general.barcode'), fields.barcode)}
                            ${renderLabeledValue(t('general.specialCode'), specialCodeValue, ' margin-top: 6px;')}
                            ${renderLabeledValue(t('general.brandName'), fields.brandName, ' margin-top: 6px;')}
                        </div>
                    </td>
                </tr>
            </table>
        </body>
        </html>
    `;
};

const buildProductCopyPlainText = (fields: ProductCopyFields, t: TFunction): string =>
    [
        `${t('general.returnReason')}: ${fields.returnReason}`,
        `${t('general.productName')}: ${fields.productName}`,
        `${t('general.productSize')}: ${fields.productSize}`,
        `${t('general.barcode')}: ${fields.barcode}`,
        fields.specialCode !== '-' ? `${t('general.specialCode')}: ${fields.specialCode}` : '',
        `${t('general.brandName')}: ${fields.brandName}`,
    ]
        .filter(Boolean)
        .join('\n');

const createHiddenEditableDiv = (html: string): HTMLDivElement => {
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'fixed';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = '1px';
    tempDiv.style.height = '1px';
    tempDiv.contentEditable = 'true';
    tempDiv.innerHTML = html;
    document.body.appendChild(tempDiv);
    return tempDiv;
};

const selectElementContents = (element: HTMLElement): Selection | null => {
    const range = document.createRange();
    range.selectNodeContents(element);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    return selection;
};

const tryLegacyExecCommandCopy = (): boolean => {
    const legacyExecCommand = (document as unknown as Record<string, unknown>)['execCommand'] as
        | undefined
        | ((commandId: string) => boolean);

    return legacyExecCommand?.('copy') ?? false;
};

const waitForImageReady = (img: HTMLImageElement): Promise<void> =>
    new Promise((resolve) => {
        if (img.complete) {
            resolve();
            return;
        }

        img.onload = () => resolve();
        img.onerror = () => resolve();
        setTimeout(() => resolve(), 1000);
    });

const copyWithClipboardApi = async (
    tempDiv: HTMLDivElement,
    html: string,
    plainText: string,
    imageDataUrl: string
): Promise<void> => {
    const img = tempDiv.querySelector('img');
    if (img && imageDataUrl) {
        await waitForImageReady(img);
    }

    const clipboardItem = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
    });
    await navigator.clipboard.write([clipboardItem]);
};

const copyHtmlToClipboard = async (html: string, plainText: string, imageDataUrl: string): Promise<void> => {
    const tempDiv = createHiddenEditableDiv(html);
    const selection = selectElementContents(tempDiv);

    try {
        if (tryLegacyExecCommandCopy()) {
            return;
        }

        throw new Error('execCommand failed');
    } catch {
        try {
            await copyWithClipboardApi(tempDiv, html, plainText, imageDataUrl);
        } catch (clipboardErr) {
            console.error('Clipboard API failed:', clipboardErr);
            throw clipboardErr;
        }
    } finally {
        selection?.removeAllRanges();
        if (document.body.contains(tempDiv)) {
            document.body.removeChild(tempDiv);
        }
    }
};

const SCUIReturnProductCopyButton: FC<SCUIReturnProductCopyButtonProps> = ({
    product,
    imageUrl,
    showLabel = true,
    iconSize = 'small',
}) => {
    const { t } = useTranslation();

    const handleCopyProductInfo = async () => {
        if (!product) {
            return;
        }

        try {
            const sourceImageUrl = imageUrl || product.productImageUrl;
            const imageDataUrl = await resolveImageDataUrl(sourceImageUrl);
            const fields = extractProductCopyFields(product);
            const html = buildProductCopyHtml(fields, imageDataUrl, t);
            const plainText = buildProductCopyPlainText(fields, t);

            await copyHtmlToClipboard(html, plainText, imageDataUrl);

            Swal.fire({
                icon: 'success',
                title: t('general.copied'),
                showConfirmButton: false,
                timer: 1500,
            });
        } catch (err) {
            console.error('Copy failed:', err);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: 'Copy failed',
            });
        }
    };

    return (
        <Tooltip title={t('general.copyToClipboard')}>
            <IconButton onClick={handleCopyProductInfo} size={iconSize} color="primary">
                <FileCopyIcon sx={{ width: 20, height: 20, ...(showLabel ? { mr: 1 } : {}) }} />
                {showLabel && <p style={{ fontSize: '14px', fontWeight: 400 }}>{t('general.copyToClipboard')}</p>}
            </IconButton>
        </Tooltip>
    );
};

export default SCUIReturnProductCopyButton;
